// SmartScheduler.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API, { matchTeachers } from './api';

/** ---------- Helpers ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
}
function addMinutes(time, mins) {
  return minutesToHHMM(toMinutes(time) + mins);
}
function simplifyIntervals(list = []) {
  return (list || []).map(x => ({
    from: normalizeTime(x.from_time ?? x.from),
    to: normalizeTime(x.to_time ?? x.to),
  })).filter(i => toMinutes(i.to) > toMinutes(i.from));
}
function subtractIntervals(base = [], blocked = []) {
  let result = simplifyIntervals(base);
  const bad = simplifyIntervals(blocked);
  for (const exc of bad) {
    const excFrom = toMinutes(exc.from), excTo = toMinutes(exc.to);
    const next = [];
    for (const it of result) {
      const a = toMinutes(it.from), b = toMinutes(it.to);
      if (excTo <= a || excFrom >= b) next.push(it);
      else {
        if (excFrom > a) next.push({ from: it.from, to: normalizeTime(exc.from) });
        if (excTo   < b) next.push({ from: normalizeTime(exc.to), to: it.to });
      }
    }
    result = next;
  }
  return result;
}
function intersectTwoSets(A = [], B = []) {
  const res = [];
  const a = simplifyIntervals(A);
  const b = simplifyIntervals(B);
  for (const i of a) {
    for (const j of b) {
      const from = Math.max(toMinutes(i.from), toMinutes(j.from));
      const to   = Math.min(toMinutes(i.to),   toMinutes(j.to));
      if (to > from) res.push({ from: minutesToHHMM(from), to: minutesToHHMM(to) });
    }
  }
  return mergeTouching(res);
}
function mergeTouching(intervals = []) {
  const sorted = intervals.slice().sort((x,y)=>toMinutes(x.from)-toMinutes(y.from));
  const out = [];
  for (const it of sorted) {
    if (!out.length) out.push({...it});
    else {
      const last = out[out.length-1];
      if (toMinutes(it.from) <= toMinutes(last.to)) {
        if (toMinutes(it.to) > toMinutes(last.to)) last.to = it.to;
      } else out.push({...it});
    }
  }
  return out;
}
function clampTo(i, startM, endM) {
  const f = Math.max(toMinutes(i.from), startM);
  const t = Math.min(toMinutes(i.to),   endM);
  if (t <= f) return null;
  return { from: minutesToHHMM(f), to: minutesToHHMM(t) };
}

// cena slota (koristi se interno, ali se za roditelja NE prikazuje)
function computeSlotTotal(from, to, teacher = {}) {
  const mins = toMinutes(to) - toMinutes(from);
  const hours = Math.max(0, mins) / 60;
  const hourly = Number(teacher.hourly_rate ?? 0);
  const travel = Number(teacher.travel_cost ?? 0);
  const bonus  = Number(teacher.bonus ?? 0);
  const total = hourly * hours + travel + bonus;
  return Math.round(total * 100) / 100;
}

/** ---------- Timeline config ---------- */
const SNAP_MIN  = 5;

/**
 * Globalni fallbacki:
 * - generički standard: 45–90
 * - event: 45–240
 * (disciplinska pravila ispod su preciznija)
 */
const MIN_DURATION_MIN = 45;
const MAX_STANDARD_MIN = 90;
const EVENT_MIN_MIN = 45;
const EVENT_MAX_MIN = 240;

/** Boje po teacher-u */
const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForTeacher(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}
function initials(name='') {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0,2).map(p=>p[0]?.toUpperCase()||'').join('');
}

/** ---------- ID helpers (id vs teacher_id) ---------- */
const getTid = (t) => String(t?.id ?? t?.teacher_id ?? t?.teacher?.id ?? '');
const getTidNum = (tOrId) => {
  if (tOrId == null) return null;
  const n = typeof tOrId === 'object' ? (tOrId.id ?? tOrId.teacher_id ?? tOrId.teacher?.id) : tOrId;
  const parsed = Number(n);
  return Number.isFinite(parsed) ? parsed : null;
};
const normalizeTeacher = (t) => {
  if (!t) return null;
  const id = t.id ?? t.teacher_id ?? t.teacher?.id;
  return { id, ...t };
};

/** ---------- Discipline / service-type labels + badge boje ---------- */
const DISCIPLINE_LABELS = {
  PROG_EI: {
    short: 'EI',
    label: 'Early Intervention',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  PROG_PI: {
    short: 'PI',
    label: 'Project Impact',
    badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  PROG_ABA: {
    short: 'ABA',
    label: 'ABA therapy',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  PROG_OT: {
    short: 'OT',
    label: 'Occupational therapy',
    badgeClass: 'bg-teal-100 text-teal-800 border-teal-200',
  },
  PROG_PT: {
    short: 'PT',
    label: 'Physical therapy',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  PROG_ST: {
    short: 'ST',
    label: 'Speech therapy',
    badgeClass: 'bg-green-100 text-green-800 border-green-200',
  },
};

function getDisciplineShort(code) {
  return DISCIPLINE_LABELS[code]?.short || code || 'Discipline';
}

/**
 * Konfig trajanja po disciplini + event – usklađeno sa TeacherDayPlanner:
 * - EI: 45 (fiksno)
 * - PI: 90 (fiksno)
 * - OT/PT: default 45, raspon 45–90
 * - ABA:  default 90, raspon 45–240
 * - Event: default 90, raspon 45–240 (nezavisno od discipline)
 * - Generic standard: default 45, raspon 45–90
 */
function getDurationConfig(progCode, isEvent) {
  if (isEvent) {
    return { min: EVENT_MIN_MIN, max: EVENT_MAX_MIN, def: 90 };
  }
  switch (progCode) {
    case 'PROG_EI':
      return { min: 45, max: 45, def: 45 };
    case 'PROG_PI':
      return { min: 90, max: 90, def: 90 };
    case 'PROG_ABA':
      return { min: 45, max: 240, def: 90 };
    case 'PROG_OT':
    case 'PROG_PT':
      return { min: 45, max: 90, def: 45 };
    case 'PROG_ST':
      return { min: 45, max: 45, def: 45 };
    default:
      // generički standard
      return { min: MIN_DURATION_MIN, max: MAX_STANDARD_MIN, def: MIN_DURATION_MIN };
  }
}

function parseServiceDurationRules(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const out = {};

  for (const [k, rule] of Object.entries(raw)) {
    if (!rule || typeof rule !== 'object') continue;

    let key = String(k || '').toUpperCase().trim();
    if (!key) continue;

    // normalize keys
    if (key.startsWith('PROG_')) key = key.replace(/^PROG_/, '');
    if (key === 'GENERIC') key = 'GENERIC';

    // ignore generic object with many unrelated keys (OBJ_MIN_TRIGGER_MIN, etc.)
    // but allow generic duration if it ever exists in the future
    // (we only read min/max/def or fixed/default-like fields)
    const type = String(rule.type || '').toLowerCase();

    // fixed
    if (type === 'fixed' || rule.fixed != null) {
      const fixed = toNum(rule.fixed ?? rule.def ?? rule.default);
      if (fixed == null || fixed <= 0) continue;
      out[key] = { min: fixed, max: fixed, def: fixed };
      continue;
    }

    // range
    if (type === 'range' || rule.min != null || rule.max != null) {
      const min = toNum(rule.min);
      const max = toNum(rule.max);
      const def = toNum(rule.default ?? rule.def ?? rule.fixed);

      if (min == null || max == null || min <= 0 || max <= 0 || max < min) continue;

      let d = def;
      if (d == null || d <= 0) d = min;
      if (d < min) d = min;
      if (d > max) d = max;

      out[key] = { min, max, def: d };
      continue;
    }
  }

  return Object.keys(out).length ? out : null;
}

/**
 * Effective cfg:
 * - prvo pokušaj iz system_settings
 * - ako nema, koristi postojeći getDurationConfig() fallback
 */
function getDurationConfigFromSettings(progCode, isEventFlag, rulesMap) {
  const fallback = getDurationConfig(progCode, isEventFlag);
  if (!rulesMap) return fallback;

  // key za lookup u rulesMap
  const key = isEventFlag
    ? 'EVENT'
    : (progCode ? String(progCode).toUpperCase().replace(/^PROG_/, '') : 'GENERIC');

  const directProgKey = progCode ? String(progCode).toUpperCase() : null;

  const cfg =
    rulesMap[key] ||
    (directProgKey ? rulesMap[directProgKey] : null) ||
    null;

  if (!cfg) return fallback;

  const min = Number.isFinite(Number(cfg.min)) ? Number(cfg.min) : fallback.min;
  const max = Number.isFinite(Number(cfg.max)) ? Number(cfg.max) : fallback.max;
  let def = Number.isFinite(Number(cfg.def)) ? Number(cfg.def) : fallback.def;

  // sanity
  const realMin = Math.max(1, min);
  const realMax = Math.max(realMin, max);
  if (def < realMin) def = realMin;
  if (def > realMax) def = realMax;

  return { min: realMin, max: realMax, def };
}

function durationText(cfg) {
  if (!cfg) return '';
  if (cfg.min === cfg.max) return `${cfg.def} min`;
  return `${cfg.min}–${cfg.max} min (default ${cfg.def} min)`;
}


/** ---------- Badges ---------- */
function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status || '—'}</span>;
}
function ReassignedBadge({ reassigned }) {
  const map = {
    pending: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  if (!reassigned || !map[reassigned]) return null;
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${map[reassigned]}`}>
      Substitute: {reassigned}
    </span>
  );
}

function DisciplineBadge({ program_code, type }) {
  if (String(type).toLowerCase() === 'event') {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
        Event
      </span>
    );
  }

  if (!program_code) return null;
  const meta = DISCIPLINE_LABELS[program_code];
  if (!meta) {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
        {program_code}
      </span>
    );
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${meta.badgeClass}`}>
      {meta.short}
    </span>
  );
}

/** ---------- Toast (efemerna poruka) ---------- */
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

/** ---------- Component ---------- */
export default function SmartScheduler({ isParent = false }) {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [overlayTeacherId, setOverlayTeacherId] = useState('');
  const [studentDefaultTeacherId, setStudentDefaultTeacherId] = useState(null);

  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  const [studentAvailByDate, setStudentAvailByDate] = useState({});
  const [teacherAvailByDate, setTeacherAvailByDate] = useState({});
  const [teacherFreeCache, setTeacherFreeCache] = useState({});

  const [slots, setSlots] = useState({});
  const [serverDateStr, setServerDateStr] = useState('');
  const [useORS] = useState(true); // za roditelje uvek ON, bez toggle-a
  const [status, setStatus] = useState(''); // ide kroz Toast

  const [matchByTeacherId, setMatchByTeacherId] = useState({});

  // modal & teacher suggestions
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [modalFrom, setModalFrom] = useState('');
  const [modalMaxTo, setModalMaxTo] = useState('');
  const [isEvent, setIsEvent] = useState(false);

  const [teacherOptions, setTeacherOptions] = useState({
    default_teacher: null,
    alternatives: [],
    orsMatrixStatus: 'SKIPPED',
    resolved_location: null,
    programRequired: null,
    programRequiredCodes: [],
    programSelected: null,
    telehealth_enabled: false,
    telehealth_label: 'Telehealth',
  });

  // Telehealth state
  const [isTelehealth, setIsTelehealth] = useState(false);
  const [telehealthLabel, setTelehealthLabel] = useState('Telehealth');
  const [showTelehealthModal, setShowTelehealthModal] = useState(false);
  const [telehealthLink, setTelehealthLink] = useState('');
  const [pendingTelehealth, setPendingTelehealth] = useState(null);

  // izabrani PROG_* za disciplinu (EI / PI / ABA / OT / PT)
  const [selectedProg, setSelectedProg] = useState(null);

  // drag
  const [drag, setDrag] = useState(null);
  const trackRefs = useRef({});

  // parent info modal
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoSlot, setInfoSlot] = useState(null);
  const [infoSlotDate, setInfoSlotDate] = useState('');

  // --- Dynamic workday (LOAD ONCE) ---
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });
  
  // --- Service duration rules from system_settings (admin-only route; silent fallback) ---
  const [serviceDurationRulesMap, setServiceDurationRulesMap] = useState(null);

  useEffect(() => {
    if (isParent) return; // parent view nema pristup ruti (403), ne spamujemo
    API.get('/service-duration-settings')
      .then(res => {
        const parsed = parseServiceDurationRules(res.data);
        setServiceDurationRulesMap(parsed);
      })
      .catch(() => {
        // silent fallback to hardcoded defaults
        setServiceDurationRulesMap(null);
      });
  }, [isParent]);
  

  useEffect(() => {
    API.get('/workday-settings')
      .then(res => setWorkday({
        start: (res.data?.start || '08:00').slice(0,5),
        end:   (res.data?.end   || '18:00').slice(0,5),
      }))
      .catch(()=>{ /* silent */ });
  }, []);

  // --- Derivatives from dynamic workday ---
  const DAY_START = workday.start;
  const DAY_END   = workday.end;
  const dayStartM = toMinutes(DAY_START);
  const dayEndM   = toMinutes(DAY_END);
  const spanM     = Math.max(1, dayEndM - dayStartM);

  // --- Local pctBox that sees dynamic values ---
  const pctBox = (i) => {
    const left = ((toMinutes(i.from) - dayStartM) / spanM) * 100;
    const width = ((toMinutes(i.to) - toMinutes(i.from)) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`
    };
  };

  // Toast auto-clear
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);
  useEffect(() => { setStatus(''); }, [selectedStudent, selectedWeekStart]);

  // current student helper
  const currentStudent = useMemo(
    () => students.find(s => String(s.id) === String(selectedStudent)) || null,
    [students, selectedStudent]
  );

  // initial load
  useEffect(() => {
    API.get('/server-time').then(res => setServerDateStr(res.data.serverTime.slice(0,10)));

    if (isParent) {
      // deca koja pripadaju TOM roditelju
      API.get('/parent/students')
        .then(res => {
          const arr = res.data || [];
          setStudents(arr);
          if (arr.length > 0) {
            setSelectedStudent(String(arr[0].id)); // prvi student kao aktivan
          }
        })
        .catch(()=>{ setStudents([]); });
    } else {
      // admin / scheduler za ceo org
      API.get('/admin/students?assigned_only=0')
        .then(res => setStudents(res.data || []))
        .catch(()=>{ setStudents([]); });
    }

    API.get('/teachers/planner').then(res => setTeachers(res.data || []));
    API.get('/weeks').then(res => setWeekOptions(res.data || []));
  }, [isParent]);

  // default teacher for selected student
  useEffect(() => {
    if (!selectedStudent) {
      setStudentDefaultTeacherId(null);
      setOverlayTeacherId('');
      return;
    }
    const st = students.find(s => String(s.id) === String(selectedStudent));
    const defT = st?.default_teacher_id || null;
    setStudentDefaultTeacherId(defT);
    // za roditelje automatski prikazujemo default teacher overlay;
    // za admina se ovo koristi dok ne izabere svog overlay teachera
    setOverlayTeacherId(defT ? String(defT) : '');
  }, [selectedStudent, students]);

  // week dates
  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`).then(res => setWeekDates(res.data || []));
  }, [selectedWeekStart]);

  // reset teacherFreeCache kad promeniš nedelju
  useEffect(() => { setTeacherFreeCache({}); }, [selectedWeekStart]);

  // student availability
  useEffect(() => {
    if (!selectedStudent || !selectedWeekStart) return;
    API.get(`/student-availability/realweek/${selectedStudent}/${selectedWeekStart}`)
      .then(res => {
        const map = {};
        Object.entries(res.data || {}).forEach(([date, obj]) => {
          const intervals = simplifyIntervals(obj.intervals || []);
          const blocked   = simplifyIntervals(obj.blocked || []);
          const free      = subtractIntervals(intervals, blocked);
          map[date] = { free, intervals, blocked };
        });
        setStudentAvailByDate(map);
      });
  }, [selectedStudent, selectedWeekStart]);

  // overlay teacher availability (vizuelno)
  async function refreshOverlayTeacher() {
    if (!overlayTeacherId || !selectedWeekStart) {
      setTeacherAvailByDate({});
      return;
    }
    try {
      const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${overlayTeacherId}`);
      const a = res.data?.availability || {};
      const e = res.data?.exceptions || {};
      const daysOff = res.data?.day_offs || [];
      const map = {};
      Object.keys(a).forEach(date => {
        const isDayOff = daysOff.includes(date);
        const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
        map[date] = { free };
      });
      const busyRes = await API.get(`/visits/teacher-week?teacher_id=${overlayTeacherId}&week_start=${selectedWeekStart}`);
      const busyByDate = busyRes.data || {};
      Object.keys(map).forEach(date => {
        const free = map[date].free || [];
        const busy = (busyByDate[date] || []).map(i => ({ from: i.from_time, to: i.to_time }));
        map[date].free = subtractIntervals(free, busy);
      });
      setTeacherAvailByDate(map);
    } catch {
      // silent
    }
  }
  useEffect(() => { refreshOverlayTeacher(); }, [overlayTeacherId, selectedWeekStart]);

  // cache teacher free for validation
  async function ensureTeacherWeekFree(teacherId, force = false) {
    if (!teacherId && teacherId !== 0) return;
    if (!force && teacherFreeCache[teacherId]) return;

    try {
      const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${teacherId}`);
      const a = res.data?.availability || {};
      const e = res.data?.exceptions || {};
      const daysOff = res.data?.day_offs || [];

      const base = {};
      Object.keys(a).forEach(date => {
        const isDayOff = daysOff.includes(date);
        const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
        base[date] = { free };
      });

      const busyRes = await API.get(`/visits/teacher-week?teacher_id=${teacherId}&week_start=${selectedWeekStart}`);
      const busyByDate = busyRes.data || {};
      Object.keys(base).forEach(date => {
        const free = base[date].free || [];
        const busy = (busyByDate[date] || []).map(i => ({ from: i.from_time, to: i.to_time }));
        base[date].free = subtractIntervals(free, busy);
      });

      setTeacherFreeCache(prev => ({ ...prev, [teacherId]: base }));
    } catch {
      setStatus('Failed to load teacher availability for validation.');
    }
  }
  function getTeacherFree(teacherId, date) {
    return teacherFreeCache?.[teacherId]?.[date]?.free || [];
  }

  function withinStudentFree(date, from, to) {
    const free = (studentAvailByDate[date]?.free || []);
    const f = toMinutes(from), t = toMinutes(to);
    return free.some(seg => f >= toMinutes(seg.from) && t <= toMinutes(seg.to));
  }
  function withinTeacherFree(date, from, to, teacherId) {
    if (teacherId == null) return true;
    const free = getTeacherFree(teacherId, date);
    const f = toMinutes(from), t = toMinutes(to);
    return free.some(seg => f >= toMinutes(seg.from) && t <= toMinutes(seg.to));
  }

  // ----- Coverage hints -----
  function coverageHint(freeSegments, from, to) {
    const fM = toMinutes(from), tM = toMinutes(to);
    const seg = freeSegments.find(s => toMinutes(s.from) <= fM && fM < toMinutes(s.to));
    if (seg) {
      const segTo = toMinutes(seg.to);
      if (segTo >= tM) return '';
      return `Teacher free until ${minutesToHHMM(segTo)} (needs until ${normalizeTime(to)})`;
    }
    const next = freeSegments.find(s => toMinutes(s.from) >= fM);
    if (next) return `Next free ${normalizeTime(next.from)}–${normalizeTime(next.to)}`;
    const prev = [...freeSegments].reverse().find(s => toMinutes(s.to) <= fM);
    if (prev) return `Last free ended at ${normalizeTime(prev.to)}`;
    return 'No free time this day';
  }
  function teacherCoverageHint(date, teacherId, from, to) {
    return coverageHint(getTeacherFree(teacherId, date), from, to);
  }
  function studentCoverageHint(date, from, to) {
    const free = (studentAvailByDate[date]?.free || []);
    return coverageHint(free, from, to);
  }

  /**
   * Predlog intervala (start/end) za dato dugme (standard ili event) + disciplinu
   * Poštuje:
   *  - min/max po disciplini/eventu (TeacherDayPlanner pravila)
   *  - modalMaxTo (intersekcija / student free klik)
   *  - radni dan (DAY_START/DAY_END)
   *  - validnost u odnosu na student i teachera
   */
  function suggestInterval(date, from, maxTo, teacherId, isEventFlag, progCode) {
    const cfg = getDurationConfigFromSettings(progCode, isEventFlag, serviceDurationRulesMap);
    
    const fromM = toMinutes(from);
    const maxByDay = Math.min(toMinutes(maxTo), dayEndM);

    let toM = fromM + cfg.def;
    if (toM > maxByDay) toM = maxByDay;

    let dur = toM - fromM;

    // minimalno trajanje
    if (dur < cfg.min) {
      toM = Math.min(fromM + cfg.min, dayEndM);
      dur = toM - fromM;
    }
    // maksimalno trajanje
    if (dur > cfg.max) {
      toM = fromM + cfg.max;
      dur = cfg.max;
    }

    const toStr = minutesToHHMM(toM);
    const okStudent = withinStudentFree(date, from, toStr);
    const okTeacher = withinTeacherFree(date, from, toStr, teacherId);

    return {
      from,
      to: toStr,
      cfg,
      okStudent,
      okTeacher,
    };
  }

  // --- Suggested Teachers ---
  async function loadMatchMap(studentId) {
    try {
      const res = await matchTeachers(studentId);
      const arr = res.data?.matches || [];
      const map = {};
      arr.forEach(r => {
        const tid = String(r.teacher_id ?? r.id);
        if (!tid) return;
        map[tid] = {
          score: Number(r.score ?? 0),
          reasons: Array.isArray(r.reasons) ? r.reasons : []
        };
      });
      setMatchByTeacherId(map);
      return map;
    } catch {
      setMatchByTeacherId({});
      return {};
    }
  }
  function getMatchForTeacher(t) {
    if (!t) return null;
    return matchByTeacherId[getTid(t)] || null;
  }

  async function loadExistingVisits() {
    if (!selectedStudent || !selectedWeekStart) { setSlots({}); return; }
    try {
      const res = await API.get(`/visits/week/${selectedStudent}/${selectedWeekStart}`);
      const m = {};
      const teacherIds = new Set();
      Object.entries(res.data || {}).forEach(([date, list]) => {
        m[date] = (list || []).map(s => {
          // --- FROM / TO sa podrškom za više naziva polja ---
          const fromRaw = s.from || s.from_time || s.start || s.start_time || null;
          const toBaseRaw = s.to || s.to_time || s.end || s.end_time || null;
          const from = normalizeTime(fromRaw);

          // robustan event detection (case-insensitive, type ili visit_type)
          const rawType = s.type || s.visit_type || '';
          const isEventSlot = String(rawType).toLowerCase() === 'event';

          const eventEndRaw = s.event_end_time || s.event_to || s.eventEnd || null;
          const endRaw = isEventSlot
            ? (eventEndRaw || toBaseRaw || fromRaw)
            : (toBaseRaw || eventEndRaw || fromRaw);

          const to = normalizeTime(endRaw || fromRaw);

          // >>> fallback na globalnu listu teachers da upotpunimo satnicu/bonus
          const teacherFromList = teachers.find(t =>
            String(t.id) === String(s.teacher_id ?? s.teacher?.id)
          );

          const teacherPayload = s.teacher || {
            id: s.teacher_id,
            teacher_id: s.teacher_id,
            name: s.teacher_name || s.provider_name || s.name || teacherFromList?.name,
            hourly_rate:
              s.hourly_rate != null
                ? s.hourly_rate
                : (teacherFromList?.hourly_rate ?? null),
            travel_cost:
              s.travel_cost != null
                ? s.travel_cost
                : 0,
            bonus:
              s.bonus != null
                ? s.bonus
                : (teacherFromList?.bonus ?? 0),
          };

          const tnorm = normalizeTeacher(teacherPayload);
          if (tnorm?.id != null) teacherIds.add(getTidNum(tnorm));

          // disciplina iz BE
          const rawProg =
            s.program_code ||
            s.discipline_code ||
            s.discipline_program_code ||
            s.discipline_prog ||
            s.service_type ||
            s.discipline_service_type ||
            null;

          let programCode = null;
          if (rawProg) {
            const up = String(rawProg).toUpperCase();
            if (up.startsWith('PROG_')) {
              programCode = up;
            } else if (['EI','PI','ABA','OT','PT','ST'].includes(up)) {
              programCode = `PROG_${up}`;
            } else {
              programCode = rawProg;
            }
          }

          const cfg = getDurationConfigFromSettings(programCode || null, isEventSlot, serviceDurationRulesMap);
          const deliveryMode = s.delivery_mode || 'in_person';
          const telehealthLink = s.telehealth_link || null;

          // clamp za Telehealth: 45–90
          let minMinutes = cfg.min;
          let maxMinutes = cfg.max;
          if (deliveryMode === 'telehealth') {
            if (minMinutes < 45) minMinutes = 45;
            if (maxMinutes > 90) maxMinutes = 90;
          }

          let baseEstimated = null;
          if (s.estimated_total != null && !Number.isNaN(Number(s.estimated_total))) {
            baseEstimated = Number(s.estimated_total);
          } else if (s.total_cost != null && !Number.isNaN(Number(s.total_cost))) {
            baseEstimated = Number(s.total_cost);
          } else {
            baseEstimated = computeSlotTotal(from, to, tnorm || {});
          }

          const effectiveType = isEventSlot ? 'event' : (s.type || s.visit_type || 'standard');

          return {
            ...s,
            teacher: tnorm,
            from,
            to,
            type: effectiveType,
            event_end_time: isEventSlot ? normalizeTime(eventEndRaw || to) : null,
            chosen: false,
            readonly: !!s.visit_id,
            invalidStudent: false,
            invalidTeacher: false,
            estimated_total: baseEstimated,
            match_score: s.match_score ?? null,
            match_reasons: Array.isArray(s.match_reasons) ? s.match_reasons : [],
            program_code: programCode,
            discipline_code: s.discipline_code ?? programCode ?? null,
            service_type: s.service_type ?? null,
            delivery_mode: deliveryMode,
            telehealth_link: telehealthLink,
            minMinutes,
            maxMinutes,
          };
        });
      });
      setSlots(m);
      for (const tid of teacherIds) {
        await ensureTeacherWeekFree(tid, true);
      }
    } catch {
      // ignore
    }
  }
  useEffect(() => { loadExistingVisits(); }, [selectedStudent, selectedWeekStart, teachers]);

  // Sada existingIntervalsByDate uključuje i snimljene i lokalne slotove
  const existingIntervalsByDate = useMemo(() => {
    const out = {};
    for (const { date } of weekDates) {
      const daySlots = slots[date] || [];
      // CANCELED NE BLOKIRA SLOToVE
      const arr = daySlots
        .filter(s => s.status !== 'canceled')
        .map(s => ({ from: s.from, to: s.to }));
      out[date] = mergeTouching(arr);
    }
    return out;
  }, [slots, weekDates]);
  

  const intersectionsByDate = useMemo(() => {
    const out = {};
    for (const { date } of weekDates) {
      const sFree = (studentAvailByDate[date]?.free || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      const tFree = (teacherAvailByDate[date]?.free || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      const inter = intersectTwoSets(sFree, tFree);
      const blocked = existingIntervalsByDate[date] || [];
      out[date] = subtractIntervals(inter, blocked);
    }
    return out;
  }, [weekDates, studentAvailByDate, teacherAvailByDate, existingIntervalsByDate]);

  function isPast(date) { return date < serverDateStr; }

  // --- Modal open + prefetch + Suggested matches + resolved_location ---
  async function openTeacherModal(date, from, to) {
    if (isParent) return; // roditelj nikada ne otvara teacher modal
    try {
      const res = await API.get(
        `/teacher-suggestions?student_id=${selectedStudent}&date=${date}&from=${from}&to=${to}&use_ors=${useORS?1:0}`
      );
      const raw = res.data || {};

      // prefetch teacher avail for week
      const ids = [];
      if (raw.default_teacher) {
        const idn = getTidNum(raw.default_teacher);
        if (idn != null) ids.push(idn);
      }
      (raw.alternatives || []).forEach(t => {
        const idn = getTidNum(t);
        if (idn != null) ids.push(idn);
      });
      await Promise.all(ids.map(id => ensureTeacherWeekFree(id)));

      const matchMap = await loadMatchMap(selectedStudent);

      const decorate = (t) => {
        if (!t) return null;
        const n = normalizeTeacher(t);
        const m = matchMap[getTid(n)] || null;
        return { ...n, _match: m };
      };

      const defaultT = decorate(raw.default_teacher);
      const alts = (raw.alternatives || [])
        .map(decorate)
        .sort((a,b) => (b?._match?.score ?? -Infinity) - (a?._match?.score ?? -Infinity));

      const programCodes = raw.programRequiredCodes || [];
      const defaultProg = programCodes[0] || null;

      setTeacherOptions({
        ...raw,
        default_teacher: defaultT,
        alternatives: alts,
        programRequiredCodes: programCodes,
      });

      setSelectedProg(defaultProg);
      setTelehealthLabel(raw.telehealth_label || 'Telehealth');
      setIsTelehealth(false);      // svaki put reset
      setIsEvent(false);           // podrazumevano standard
      setModalDate(date);
      setModalFrom(from);
      setModalMaxTo(to);
      setShowModal(true);
    } catch (e) {
      setStatus(e.response?.data?.error || 'Failed to load available teachers');
    }
  }

  // klik po student free/intersection segmentu
  function openFromStudentFreeClick(date, seg, e) {
    if (isParent) return; // roditelj ne može da dodaje slot
    const track = trackRefs.current[date];
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    let minuteAtX = dayStartM + Math.round(((clientX - rect.left) / rect.width) * spanM / SNAP_MIN) * SNAP_MIN;

    const segFromM = toMinutes(seg.from);
    const segToM   = toMinutes(seg.to);
    // minimalno trajanje – koristimo 45 kao bazu (isti fallback kao TeacherDayPlanner)
    const MIN_FALLBACK = MIN_DURATION_MIN;

    minuteAtX = Math.max(segFromM, Math.min(segToM - MIN_FALLBACK, minuteAtX));
    if (minuteAtX + MIN_FALLBACK > segToM) {
      minuteAtX = Math.max(segFromM, segToM - MIN_FALLBACK);
    }
    const start = minutesToHHMM(minuteAtX);

    openTeacherModal(date, start, seg.to);
  }

  function recomputeValidity(date, item) {
    if (item.readonly || item.visit_id) return { invalidStudent: false, invalidTeacher: false };
    const invalidStudent = !withinStudentFree(date, item.from, item.to);
    const invalidTeacher = item.teacher
      ? !withinTeacherFree(date, item.from, item.to, getTidNum(item.teacher))
      : false;
    return { invalidStudent, invalidTeacher };
  }

  function addLocalSlot(
    date,
    from,
    to,
    teacher,
    slotType = 'standard',
    progCode = null,
    durationCfg,
    deliveryMode = 'in_person',
    teleLink = null
  ) {
    const tnorm = normalizeTeacher(teacher);
    const m = getMatchForTeacher(tnorm);
    const tidNum = getTidNum(tnorm);

    const isEventSlot = slotType === 'event';
    const cfg = durationCfg || getDurationConfigFromSettings(progCode, isEventSlot, serviceDurationRulesMap);

    let minMinutes = cfg.min ?? (isEventSlot ? EVENT_MIN_MIN : MIN_DURATION_MIN);
    let maxMinutes = cfg.max ?? (isEventSlot ? EVENT_MAX_MIN : MAX_STANDARD_MIN);

    // Telehealth: uvek 45–90 min (bez obzira na disciplinu)
    if (deliveryMode === 'telehealth') {
      if (minMinutes < 45) minMinutes = 45;
      if (maxMinutes > 90) maxMinutes = 90;
    }

    setSlots(prev => {
      const copy = { ...prev };
      const list = (copy[date] || []).slice();

      // 0) EVENT: samo jedan event dnevno po studentu (ali samo aktivni, ne canceled)
      if (isEventSlot) {
        const existingEventsCount = list.filter(
          s => s.status !== 'canceled' && String(s.type).toLowerCase() === 'event'
        ).length;
        if (existingEventsCount >= 1) {
          setStatus(
            `On ${date} this student already has an event visit. Only one event per day is allowed.`
          );
          return prev;
        }
      }

      // 1) disciplina: jedna poseta dnevno po disciplini (samo aktivne, ne canceled)
      if (!isEventSlot && progCode) {
        const hasDisc = list.some(
          s =>
            s.status !== 'canceled' &&
            String(s.type).toLowerCase() !== 'event' &&
            s.program_code === progCode
        );
        if (hasDisc) {
          setStatus(
            `On ${date} this student already has a visit for ${getDisciplineShort(
              progCode
            )}. Only one visit per discipline per day is allowed.`
          );
          return prev;
        }
      }

      // 2) zabrana preklapanja – canceled NE računamo
      const f = toMinutes(from);
      const t = toMinutes(to);

      const overlaps = list.some(s => {
        if (s.status === 'canceled') return false;
        const sf = toMinutes(s.from);
        const st = toMinutes(s.to);
        return Math.max(f, sf) < Math.min(t, st);
      });

      if (overlaps) {
        setStatus('Cannot place this visit: it overlaps an existing visit on this day.');
        return prev;
      }

      const base = {
        from,
        to,
        teacher: tnorm,
        type: isEventSlot ? 'event' : 'standard',
        program_code: isEventSlot ? null : (progCode || null),
        delivery_mode: deliveryMode,
        telehealth_link: teleLink,
      };
      const validity = recomputeValidity(date, base);

      list.push({
        ...base,
        original_teacher_id: studentDefaultTeacherId,
        reassigned_status: tidNum === studentDefaultTeacherId ? 'none' : 'pending',
        reassigned_response_time: tidNum === studentDefaultTeacherId ? null : new Date().toISOString(),
        status: 'draft',
        visit_id: null,
        event_end_time: isEventSlot ? to : null,
        chosen: false,
        readonly: false,
        invalidStudent: validity.invalidStudent,
        invalidTeacher: validity.invalidTeacher,
        estimated_total: computeSlotTotal(from, to, tnorm),
        match_score: m?.score ?? null,
        match_reasons: Array.isArray(m?.reasons) ? m.reasons : [],
        minMinutes,
        maxMinutes,
      });

      copy[date] = list;
      return copy;
    });
  }

  async function selectTeacherInModal(t) {
    const tnorm = normalizeTeacher(t);
    const tidNum = getTidNum(tnorm);
    const from = modalFrom;

    const effectiveProg = isEvent ? null : (selectedProg || null);

    // TELEHEALTH: dodatna zaštita – student mora imati aktivnu tu disciplinu
    if (isTelehealth && !isEvent) {
      const codes = teacherOptions.programRequiredCodes || [];
      if (!effectiveProg || !codes.includes(effectiveProg)) {
        setStatus(
          'Selected Telehealth discipline is not assigned for this student. Please choose a valid discipline.'
        );
        return;
      }
    }

    const suggestion = suggestInterval(
      modalDate,
      from,
      modalMaxTo,
      tidNum,
      isEvent,
      effectiveProg
    );

    if (!suggestion.okStudent || !suggestion.okTeacher) return;

    // disciplina: jedna dnevno – ostaje safety check i ovde (standard)
    if (!isEvent && effectiveProg) {
      const daySlots = slots[modalDate] || [];
      const hasDisc = daySlots.some(
        s => s.status !== 'canceled' && String(s.type).toLowerCase() !== 'event' && s.program_code === effectiveProg
      );
      if (hasDisc) {
        setStatus(
          `On ${modalDate} this student already has a visit for ${getDisciplineShort(
            effectiveProg
          )}. Only one visit per discipline per day is allowed.`
        );
        return;
      }
    }

    // EVENT: još jedan safety check – da ne otvaramo teleport koji će se odbiti tek u addLocalSlot
    if (isEvent) {
      const daySlots = slots[modalDate] || [];
      const existingEventsCount = daySlots.filter(s => s.status !== 'canceled' && String(s.type).toLowerCase() === 'event').length;
      if (existingEventsCount >= 1) {
        setStatus(
          `On ${modalDate} this student already has an event visit. Only one event per day is allowed.`
        );
        return;
      }
    }

    const daySlotsExisting = slots[modalDate] || [];
    const f = toMinutes(suggestion.from);
    const tM = toMinutes(suggestion.to);
    const overlaps = daySlotsExisting.some(s => {
      if (s.status === 'canceled') return false;
      const sf = toMinutes(s.from);
      const st = toMinutes(s.to);
      return Math.max(f, sf) < Math.min(tM, st);
    });
    if (overlaps) {
      setStatus('Cannot place this visit: it overlaps an existing visit on this day.');
      return;
    }

    await ensureTeacherWeekFree(tidNum);
    const slotType = isEvent ? 'event' : 'standard';

    // TELEHEALTH flow: teacher mora imati can_telehealth i student ENV_TELEHEALTH
    if (isTelehealth) {
      if (!teacherOptions.telehealth_enabled || !tnorm.can_telehealth) {
        setStatus('This teacher is not enabled for Telehealth with this student.');
        return;
      }

      // otvaramo mali modal za unos linka
      setPendingTelehealth({
        date: modalDate,
        from: suggestion.from,
        to: suggestion.to,
        teacher: tnorm,
        slotType,
        prog: effectiveProg,
        cfg: suggestion.cfg,
      });
      setTelehealthLink('');
      setShowModal(false);          // zatvori teacher modal
      setShowTelehealthModal(true); // otvori telehealth modal
      return;
    }

    // klasična (in-person / event) poseta
    addLocalSlot(
      modalDate,
      suggestion.from,
      suggestion.to,
      tnorm,
      slotType,
      effectiveProg,
      suggestion.cfg,
      'in_person',
      null
    );
    setShowModal(false);
    setSelectedProg(null);
    setIsEvent(false);
  }

  // --- DELETE (saved + local) ---
  async function handleDeleteSlot(date, idx) {
    const item = (slots[date] || [])[idx];
    if (!item) return;

    if (item.visit_id) {
      const ok = window.confirm('Are you sure you want to permanently delete this SAVED and COMPLETED visit?');
      if (!ok) return;
      try {
        await API.delete(`/visits/${item.visit_id}`);
        setSlots(prev => {
          const copy = { ...prev };
          const list = (copy[date] || []).slice();
          list.splice(idx, 1);
          copy[date] = list;
          return copy;
        });
        setStatus('Visit deleted');

        const tid = getTidNum(item.teacher);
        if (tid != null) {
          await ensureTeacherWeekFree(tid, true);
          if (String(tid) === String(overlayTeacherId)) {
            await refreshOverlayTeacher();
          }
        }
      } catch {
        setStatus('Failed to delete visit');
      }
      return;
    }

    // lokalni kandidat
    setSlots(prev => {
      const copy = { ...prev };
      const list = (copy[date] || []).slice();
      list.splice(idx, 1);
      copy[date] = list;
      return copy;
    });
  }

  function validateBeforeSave() {
    for (const { date } of weekDates) {
      const list = slots[date] || [];
      if (!list.length) continue;

      // osnovne validacije za sve nove (unsaved) slotove
      for (const s of list) {
        if (s.visit_id) continue;

        const dur = toMinutes(s.to) - toMinutes(s.from);
        const slotMin = s.minMinutes ?? (s.type === 'event' ? EVENT_MIN_MIN : MIN_DURATION_MIN);
        const slotMax = s.maxMinutes ?? (s.type === 'event' ? EVENT_MAX_MIN : MAX_STANDARD_MIN);

        if (s.invalidStudent) {
          return `Slot on ${date} ${s.from}-${s.to} is outside STUDENT availability.`;
        }
        // ⚠️ invalidTeacher je sada samo upozorenje (vizuelno),
        // ne blokira snimanje:
        // if (s.invalidTeacher) {
        //   return `Slot on ${date} ${s.from}-${s.to} is outside TEACHER availability.`;
        // }

        if (dur < slotMin) {
          return `Slot on ${date} must be at least ${slotMin} minutes.`;
        }
        if (dur > slotMax) {
          return `Slot on ${date} must be at most ${slotMax} minutes.`;
        }
      }

      // disciplina: jedna poseta dnevno po disciplini (samo standard, nove + postojeće)
      const perDisc = {};
      let eventCountForDay = 0; // <<< NOVO: broj event-ova tog dana

      for (const s of list) {
        if (s.status === 'canceled') continue;
        if (String(s.type).toLowerCase() === 'event') {
          eventCountForDay += 1;
          continue;
        }
        const code = s.program_code;
        if (!code) continue;
        perDisc[code] = (perDisc[code] || 0) + 1;
      }

      for (const [code, count] of Object.entries(perDisc)) {
        if (count > 1) {
          return `On ${date} discipline ${getDisciplineShort(code)} has ${count} visits. Only one per day is allowed.`;
        }
      }

      // EVENT: samo jedan event dnevno po studentu (sačuvani + draft zajedno)
      if (eventCountForDay > 1) {
        return `On ${date} this student has ${eventCountForDay} event visits. Only one event per day is allowed.`;
      }

      // sigurnosna provera preklapanja svih slotova tog dana
      const sorted = list
        .filter(s => s.status !== 'canceled')
        .map((s, idx) => ({ ...s, _idx: idx }))
        .sort((a,b) => toMinutes(a.from) - toMinutes(b.from));
      for (let i = 0; i < sorted.length - 1; i++) {
        const curEnd = toMinutes(sorted[i].to);
        const nextStart = toMinutes(sorted[i+1].from);
        if (nextStart < curEnd) {
          return `On ${date} visits overlap around ${sorted[i].to}.`;
        }
      }
    }
    return '';
  }

  const preSaveError = useMemo(() => validateBeforeSave(), [slots, weekDates]);

  const hasAnythingToSave = useMemo(() => {
    for (const { date } of weekDates) {
      const list = slots[date] || [];
      if (list.some(s => !s.visit_id)) return true;
    }
    return false;
  }, [slots, weekDates]);

  async function handleSave() {
    const err = validateBeforeSave();
    if (err) {
      setStatus(err);
      return;
    }

    if (!selectedStudent) {
      setStatus('Please select a student first.');
      return;
    }

    const visits = [];

    for (const { date } of weekDates) {
      const list = slots[date] || [];
      for (const s of list) {
        if (s.visit_id) continue;

        const tid = getTidNum(s.teacher);
        if (!tid) continue;

        const endTimeStr = `${date} ${normalizeTime(s.to)}`;

        visits.push({
          student_id: Number(selectedStudent),
          teacher_id: tid,
          scheduled_at: `${date} ${normalizeTime(s.from)}`,
          notes: s.notes || '',
          visit_id: null,
          status: s.status || 'draft',
          type: s.type || 'standard',
          event_end_time: endTimeStr,
          original_teacher_id: s.original_teacher_id ?? studentDefaultTeacherId ?? tid,
          reassigned_status: s.reassigned_status || 'none',
          discipline_code: s.program_code || null,
          delivery_mode: s.delivery_mode || 'in_person',
          telehealth_link: s.telehealth_link || null,
        });
      }
    }

    if (!visits.length) {
      setStatus('Nothing to save.');
      return;
    }

    try {
      const res = await API.post('/visits/bulk', { visits });
      if (!res.data?.success) {
        setStatus(res.data?.error || 'Save failed.');
        return;
      }

      setStatus('Saved.');
      await loadExistingVisits();
      const teacherIds = new Set();
      for (const { date } of weekDates) {
        (slots[date] || []).forEach(s => {
          const tid = getTidNum(s.teacher);
          if (tid != null) teacherIds.add(tid);
        });
      }
      for (const tid of teacherIds) {
        await ensureTeacherWeekFree(tid, true);
      }
      if (overlayTeacherId) {
        await refreshOverlayTeacher();
      }
    } catch (e) {
      setStatus(e?.response?.data?.error || 'Save failed.');
    }
  }

  /** ---------- Parent info modal helpers ---------- */
  function openInfoModal(date, s) {
    if (!isParent) return;
    setInfoSlot({ ...s });
    setInfoSlotDate(date);
    setShowInfoModal(true);
  }

  /** ---------- Drag & Resize ---------- */
  function startDrag(e, date, idx, mode) {
    if (isParent) return; // roditelj ne može da pomera slot
    const track = trackRefs.current[date];
    if (!track) return;
    const item = (slots[date] || [])[idx];
    if (!item || item.readonly || item.visit_id) return;

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    e.preventDefault();
    setDrag({
      date, idx, mode,
      startX: clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      origFrom: item.from,
      origTo: item.to
    });
  }

  function onMove(e) {
    if (!drag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = clientX - drag.rectLeft;
    const minuteAtX = dayStartM + Math.round((dx / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;

    setSlots(prev => {
      const copy = { ...prev };
      const list = copy[drag.date].slice();
      const item = { ...list[drag.idx] };

      if (item.readonly || item.visit_id) return prev;

      const slotMin = item.minMinutes ?? (item.type === 'event' ? EVENT_MIN_MIN : MIN_DURATION_MIN);
      const slotMax = item.maxMinutes ?? (item.type === 'event' ? EVENT_MAX_MIN : MAX_STANDARD_MIN);

      const origDur = toMinutes(drag.origTo) - toMinutes(drag.origFrom);
      let newFrom = toMinutes(item.from);
      let newTo   = toMinutes(item.to);

      if (drag.mode === 'move') {
        const anchorAtStart = dayStartM + Math.round(((drag.startX - drag.rectLeft) / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;
        const anchorOffset = toMinutes(drag.origFrom) - anchorAtStart;
        newFrom = Math.max(dayStartM, Math.min(dayEndM - origDur, minuteAtX + anchorOffset));
        newTo   = newFrom + origDur;
      } else if (drag.mode === 'resize-left') {
        const maxLeft = toMinutes(item.to) - slotMin;
        newFrom = Math.max(dayStartM, Math.min(maxLeft, minuteAtX));
      } else if (drag.mode === 'resize-right') {
        const minRight = toMinutes(item.from) + slotMin;
        newTo = Math.max(minRight, Math.min(dayEndM, minuteAtX));
      }

      let dur = newTo - newFrom;

      if (dur < slotMin) {
        if (drag.mode === 'resize-left') {
          newFrom = newTo - slotMin;
        } else if (drag.mode === 'resize-right') {
          newTo = newFrom + slotMin;
        }
        dur = newTo - newFrom;
      }

      if (dur > slotMax) {
        if (drag.mode === 'resize-left') {
          newFrom = newTo - slotMax;
        } else if (drag.mode === 'resize-right') {
          newTo = newFrom + slotMax;
        } else {
          // kod move ostaje u originalnom trajanju
        }
      }

      newFrom = Math.max(dayStartM, newFrom);
      newTo   = Math.min(dayEndM, newTo);

      const f = newFrom;
      const t = newTo;
      const overlaps = list.some((other, j) => {
        if (j === drag.idx) return false;
        if (other.status === 'canceled') return false;
        const sf = toMinutes(other.from);
        const st = toMinutes(other.to);
        return Math.max(f, sf) < Math.min(t, st);
      });
      if (overlaps) {
        return prev;
      }

      item.from = minutesToHHMM(newFrom);
      item.to   = minutesToHHMM(newTo);
      const validity = recomputeValidity(drag.date, item);
      item.invalidStudent = validity.invalidStudent;
      item.invalidTeacher = validity.invalidTeacher;
      item.estimated_total = computeSlotTotal(item.from, item.to, item.teacher);

      list[drag.idx] = item;
      copy[drag.date] = list;
      return copy;
    });
  }
  function endDrag() { setDrag(null); }

  useEffect(() => {
    function up(){ endDrag(); }
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up, { passive: false });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
    };
  }, [drag]);

  /** ---------- UI ---------- */
  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={()=>setStatus('')} />

      <h2 className="text-lg font-bold mb-4">
        {isParent
          ? 'Smart Scheduler (parent view – read only)'
          : 'Smart Scheduler (drag, multi-teacher, multi-discipline + event + telehealth)'}
      </h2>

      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <label className="block mb-1">Student:</label>
          {isParent ? (
            currentStudent ? (
              <div className="border px-3 py-2 rounded bg-gray-50 font-semibold">
                {(currentStudent.last_name || '') + ', ' + (currentStudent.first_name || '')}
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No child assigned to this parent account.
              </div>
            )
          ) : (
            <select
              className="border px-2 py-1 w-64"
              value={selectedStudent}
              onChange={e=>setSelectedStudent(e.target.value)}
            >
              <option value="">-- Choose student --</option>
              {students
                .slice()
                .sort(
                  (a,b)=>
                    (a.last_name||'').localeCompare(b.last_name||'') ||
                    (a.first_name||'').localeCompare(b.first_name||'')
                )
                .map(s=>(
                  <option key={s.id} value={s.id}>
                    {(s.last_name||'')+', '+(s.first_name||'')}
                  </option>
                ))}
            </select>
          )}
        </div>

        {!!selectedStudent && (
          <>
            <div>
              <label className="block mb-1">Week:</label>
              <select
                className="border px-2 py-1 w-56"
                value={selectedWeekStart}
                onChange={e=>setSelectedWeekStart(e.target.value)}
              >
                <option value="">-- Choose week --</option>
                {weekOptions.map((w,i)=><option key={i} value={w.value}>{w.label}</option>)}
              </select>
            </div>

            {!isParent && (
              <div>
                <label className="block mb-1">Overlay teacher:</label>
                <select
                  className="border px-2 py-1 w-64"
                  value={overlayTeacherId}
                  onChange={e=>setOverlayTeacherId(e.target.value)}
                >
                  <option value="">{studentDefaultTeacherId ? '(use default)' : '-- choose --'}</option>
                  {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {studentDefaultTeacherId && !overlayTeacherId && (
                  <div className="text-xs text-gray-500 mt-1">Using default teacher of selected student.</div>
                )}
              </div>
            )}

            {!isParent && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Travel cost (slower)</span>
                {/* ADMIN toggle – za parent ne prikazujemo */}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm mb-3">
        <span className="flex items-center gap-2">
          <span className="inline-block w-5 h-3 rounded bg-green-300" /> Student free
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-5 h-3 rounded bg-blue-300" /> Teacher free
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-5 h-3 rounded bg-teal-400" /> Intersection (admin click to add)
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-5 h-3 rounded bg-gray-300 border" /> Candidate (drag / resize – admin only)
        </span>
      </div>

      {weekDates.length > 0 && (
        <div className="space-y-4">
          {weekDates.map(({date, dayName})=>{
            const sFree = (studentAvailByDate[date]?.free || [])
              .map(i => clampTo(i, dayStartM, dayEndM))
              .filter(Boolean);
            const tFree = (teacherAvailByDate[date]?.free || [])
              .map(i => clampTo(i, dayStartM, dayEndM))
              .filter(Boolean);
            const inter = intersectionsByDate[date] || [];
            const daySlots = slots[date] || [];
            const isPastDay = isPast(date);

            const savedCount = daySlots.filter(s=>!!s.visit_id).length;
            const draftCount = daySlots.filter(s=>!s.visit_id).length;

            return (
              <div key={date} className="border rounded p-3 shadow-sm">
                <div
                  className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${
                    isPastDay ? 'text-gray-400 bg-gray-50' : 'text-gray-800 bg-gray-100'
                  }`}
                >
                  <span>{dayName} – {date}</span>
                  {savedCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                      Saved {savedCount}
                    </span>
                  )}
                  {draftCount > 0 && !isParent && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">
                      Draft {draftCount}
                    </span>
                  )}
                </div>

                <div
                  className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                  ref={el => (trackRefs.current[date] = el)}
                  data-date={date}
                >
                  {/* satni tick-ovi */}
                  {Array.from({length: (spanM/60)+1}).map((_,i)=>{
                    const leftPct = (i*60)/spanM*100;
                    return (
                      <div key={i}>
                        <div className="absolute top-4 bottom-0 border-l border-gray-300" style={{ left: `${leftPct}%` }} />
                        <div className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2" style={{ left: `${leftPct}%` }}>
                          {minutesToHHMM(dayStartM + i*60).slice(0,2)}
                        </div>
                      </div>
                    );
                  })}

                  {/* student free */}
                  {sFree.map((seg,idx)=>{
                    const box = pctBox(seg);
                    const clickable = !isPastDay && !isParent;
                    return clickable ? (
                      <button
                        key={'s'+idx}
                        className="absolute top-4 bottom-1 bg-green-300 opacity-30 rounded hover:opacity-60"
                        style={{left:box.left,width:box.width}}
                        title={`Student free ${seg.from}–${seg.to} (click to choose start time)`}
                        onClick={(e)=>openFromStudentFreeClick(date, seg, e)}
                      />
                    ) : (
                      <div
                        key={'s'+idx}
                        className="absolute top-4 bottom-1 bg-green-300 opacity-20 rounded"
                        style={{left:box.left,width:box.width}}
                      />
                    );
                  })}

                  {/* teacher free */}
                  {tFree.map((seg,idx)=>{
                    const box = pctBox(seg);
                    return (
                      <div
                        key={'t'+idx}
                        className="absolute top-4 bottom-1 bg-blue-300 opacity-20 rounded"
                        style={{left:box.left,width:box.width}}
                      />
                    );
                  })}

                  {/* intersections – podignuti iznad canceled slota */}
                  {inter.map((seg,idx)=>{
                    const box = pctBox(seg);
                    const clickable = !isPastDay && !isParent;
                    const interStyle = { left: box.left, width: box.width, zIndex: 20 }; // Z-INDEX tuning
                    return clickable ? (
                      <button
                        key={'i'+idx}
                        className="absolute top-4 bottom-1 bg-teal-400/40 hover:bg-teal-400/50 rounded"
                        style={interStyle}
                        title={`Intersection ${seg.from}–${seg.to} (click to choose start time)`}
                        onClick={(e)=>openFromStudentFreeClick(date, seg, e)}
                      />
                    ) : (
                      <div
                        key={'i'+idx}
                        className="absolute top-4 bottom-1 bg-teal-400/20 rounded opacity-40"
                        style={interStyle}
                      />
                    );
                  })}

                  {/* kandidati + saved */}
                  {daySlots.map((s, idx)=>{
                    const box = pctBox(s);
                    const teacherColor = colorForTeacher(getTid(s.teacher));
                    const anyInvalid = (s.readonly || s.visit_id) ? false : (s.invalidStudent || s.invalidTeacher);
                    const isCanceled = String(s.status || '').toLowerCase() === 'canceled';
					const isComplete = String(s.status || '').toLowerCase() === 'complete';

                    // vizuelni stil slota
                    let containerBorder = 'border-gray-400';
                    let containerOpacity = 'opacity-90';
                    let containerBg = 'bg-white';
                    let innerColorClass = teacherColor;
                    let innerOpacity = 'opacity-40';

                    if (s.readonly || s.visit_id) {
                      containerBorder = 'border-emerald-700';
                      containerOpacity = 'opacity-100';
                    }
                    if (anyInvalid && !(s.readonly || s.visit_id)) {
                      containerBorder = 'border-red-500';
                      containerOpacity = 'opacity-90';
                    }
					if (isComplete) {
					    containerBorder = 'border-gray-500';
					    containerBg = 'bg-gray-100';
					    innerColorClass = 'bg-gray-300';
					    innerOpacity = 'opacity-40';
					}
                    // GHOST efekat za canceled vizite
                    if (isCanceled) {
                      containerBorder = 'border-purple-400 border-dashed';
                      containerOpacity = 'opacity-90';
                      containerBg = 'bg-gray-50';
                      innerColorClass = 'bg-gray-300';
                      innerOpacity = 'opacity-100';
                    }

                    // Z-INDEX tuning for canceled vs active vs intersections
                    const slotStyle = {
                      left: box.left,
                      width: box.width,
                      zIndex: isCanceled ? 10 : 30,
                    };

                    return (
                      <div
                        key={'c'+idx}
                        className={`absolute top-5 bottom-2 ${containerOpacity} rounded border ${containerBorder} shadow-sm overflow-hidden ${containerBg}`}
                        style={slotStyle}
                        onClick={isParent ? () => openInfoModal(date, s) : undefined}
                      >
                        <div className={`${innerColorClass} h-full ${innerOpacity}`} />
                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                          <span className={`font-mono ${isCanceled ? 'line-through text-gray-500' : ''}`}>
                            {s.from}–{s.to}
                          </span>
                          <span className={`hidden sm:inline font-semibold ${isCanceled ? 'text-gray-500' : ''}`}>
                            {s.teacher?.name || '—'}
                          </span>
                          <span className={`sm:hidden font-semibold ${isCanceled ? 'text-gray-500' : ''}`}>
                            {initials(s.teacher?.name || '')}
                          </span>
                        </div>

                        {/* X dugme za delete – ADMIN only */}
                        {!isPastDay && !isParent && (
                          <button
                            className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                            title="Delete"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteSlot(date, idx);
                            }}
                          >×</button>
                        )}

                        {/* Drag & resize – samo za admin, ne za roditelja i ne za saved */}
                        {!isPastDay && !isParent && !(s.readonly || s.visit_id) && (
                          <>
                            <div
                              className="absolute inset-0 cursor-grab"
                              onMouseDown={(e)=>startDrag(e,date,idx,'move')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'move')}
                            />
                            <div
                              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize"
                              onMouseDown={(e)=>startDrag(e,date,idx,'resize-left')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'resize-left')}
                            />
                            <div
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize"
                              onMouseDown={(e)=>startDrag(e,date,idx,'resize-right')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'resize-right')}
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {(daySlots.length > 0) && (
                  <div className="mt-2 space-y-1">
                    {daySlots.map((s, idx)=>{
                      const isCanceledRow = String(s.status || '').toLowerCase() === 'canceled';
                      return (
                        <div
                          key={'l'+idx}
                          className={
                            "flex items-center justify-between text-sm " +
                            (isCanceledRow ? "opacity-60 italic" : "")
                          }
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-block w-3 h-3 rounded-full ${colorForTeacher(getTid(s.teacher))}`} />
                            <span className={`font-semibold ${isCanceledRow ? 'text-gray-500' : ''}`}>
                              {s.teacher?.name || '—'}
                            </span>

                            {/* ADMIN: prikaz cene; PARENT: sakriveno */}
                            {!isParent &&
                              typeof s.estimated_total === 'number' &&
                              !Number.isNaN(s.estimated_total) &&
                              s.estimated_total !== 0 && (
                                <span className="px-2 py-0.5 rounded bg-gray-100 border text-xs">
                                  ${s.estimated_total.toFixed(2)}
                                </span>
                            )}

                            <span className={`font-mono ${isCanceledRow ? 'line-through text-gray-500' : ''}`}>
                              {s.from}–{s.to}
                            </span>

                            <DisciplineBadge program_code={s.program_code} type={s.type} />
                            <StatusBadge status={s.status || 'draft'} />
                            <ReassignedBadge reassigned={s.reassigned_status} />

                            {/* Telehealth indikator + link */}
                            {s.delivery_mode === 'telehealth' && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-xs">
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect x="3" y="5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                                  <path d="M17 9L21 7V17L17 15V9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                </svg>
                                <span>Telehealth</span>
                              </span>
                            )}
                            {s.delivery_mode === 'telehealth' && s.telehealth_link && (
                              <a
                                href={s.telehealth_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 underline break-all max-w-[160px] truncate"
                                title={s.telehealth_link}
                              >
                                Join link
                              </a>
                            )}

                            {!(s.readonly || s.visit_id) && s.invalidStudent && (
                              <span className="text-red-600 ml-1">
                                outside <b>student</b> availability
                              </span>
                            )}
                            {!(s.readonly || s.visit_id) && s.invalidTeacher && (
                              <span className="text-orange-600 ml-1">
                                outside <b>teacher</b> availability
                              </span>
                            )}
                            {(s.readonly || s.visit_id) && (
                              <span className="ml-1 text-emerald-700 text-xs font-semibold">
                                saved
                              </span>
                            )}

                            {typeof s.match_score === 'number' && !isParent && (
                              <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs">
                                Match {s.match_score}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Delete dugme – samo za admin/scheduler */}
                            {!isParent && (
                              <button
                                className={
                                  `px-2 py-0.5 rounded border text-red-600 ` +
                                  (isPast(date) ? 'opacity-50 cursor-not-allowed' : '')
                                }
                                onClick={()=>!isPast(date) && handleDeleteSlot(date, idx)}
                                disabled={isPast(date)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Save dugme – nema ga u parent view-u */}
      {selectedWeekStart && !isParent && (
        <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:items-center">
          <button
            onClick={handleSave}
            className={
              `px-4 py-2 rounded text-white ` +
              ((!hasAnythingToSave || preSaveError)
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700')
            }
            disabled={!hasAnythingToSave || !!preSaveError}
            title={
              !hasAnythingToSave
                ? 'No new visits to save'
                : (preSaveError || '')
            }
          >
            Save all new visits
          </button>

          {!hasAnythingToSave && (
            <div className="text-sm text-gray-600">
              Nothing to save — add at least one new visit.
            </div>
          )}
          {!!preSaveError && (
            <div className="text-sm text-red-600">
              {preSaveError}
            </div>
          )}
        </div>
      )}

      {/* Modal: Select Teacher (ADMIN only, parent nikad ne dolazi ovde jer je openTeacherModal blokiran) */}
      {showModal && !isParent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={()=>{
                setShowModal(false);
                setSelectedProg(null);
                setIsEvent(false);
                setIsTelehealth(false);
              }}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2">Select Teacher</h3>

            {/* Travel info i matrix status – ADMIN only */}
            {teacherOptions.resolved_location && (
              <div className="text-xs text-gray-700 mb-2">
                Using student{' '}
                <b>
                  {teacherOptions.resolved_location === 'alt'
                    ? 'ALT address'
                    : 'PRIMARY address'}
                </b>{' '}
                for travel distance calculation.
              </div>
            )}

            {teacherOptions.orsMatrixStatus === 'SKIPPED' && (
              <div className="text-blue-700 text-xs mb-2">
                <b>Travel cost calculation skipped</b> for faster loading.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'FAILED' && (
              <div className="text-red-700 text-xs mb-2">
                <b>Travel cost calculation failed</b> – showing estimated costs only.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_STUDENT_COORD' && (
              <div className="text-yellow-700 text-xs mb-2">
                Cannot calculate travel cost – student has no valid coordinates for this slot.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_CANDIDATES' && (
              <div className="text-yellow-700 text-xs mb-2">
                No teachers with known locations for travel cost matrix – showing hourly+bonus only.
              </div>
            )}

            {/* Discipline + EVENT + TELEHEALTH dugmad */}
            <div className="mt-2 mb-3">
              <div className="text-xs font-semibold mb-1">
                Visit discipline / service type
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {Object.entries(DISCIPLINE_LABELS).map(([prog, meta]) => {
                  const hasStudentDisc =
                    (teacherOptions.programRequiredCodes || []).includes(prog);

                  // AKTIVNOST: discipline ostaje obojena i kada je Telehealth uključen
                  const active = !isEvent && selectedProg === prog;
                  const disabled = !hasStudentDisc;

                  return (
                    <button
                      key={prog}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setIsEvent(false);
                        // kada promeniš disciplinu, Telehealth i dalje ostaje vezan za novu disciplinu
                        setSelectedProg(prog);
                      }}
                      className={
                        "px-2 py-0.5 rounded-full text-xs border " +
                        (disabled
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : active
                            ? meta.badgeClass + ' font-semibold'
                            : "bg-white text-gray-800 border-gray-300 hover:bg-blue-50")}
							title={
							  hasStudentDisc
							    ? `${meta.label} • ${durationText(getDurationConfigFromSettings(prog, false, serviceDurationRulesMap))}`
							    : `${meta.short} is not assigned for this student`
							}
                      
                    >
                      {meta.short}
                    </button>
                  );
                })}

                {/* EVENT dugme */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !isEvent;
                    setIsEvent(next);
                    if (next) {
                      setIsTelehealth(false);
                      setSelectedProg(null);
                    }
                  }}
                  className={
                    "px-2 py-0.5 rounded-full text-xs border " +
                    (isEvent
                      ? "bg-yellow-500 text-white border-yellow-500"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-yellow-50")
                  }
				  title={`Event visit (multi-hour, ${durationText(getDurationConfigFromSettings(null, true, serviceDurationRulesMap))})`}
                  
                >
                  Event
                </button>

                {/* TELEHEALTH dugme */}
                <button
                  type="button"
                  onClick={() => {
                    if (!teacherOptions.telehealth_enabled) return;
                    const next = !isTelehealth;

                    if (next) {
                      // uključujemo Telehealth
                      if (!isEvent) {
                        // Telehealth za standardnu disciplinu – mora postojati izabrana i aktivna disciplina
                        if (!selectedProg) {
                          setStatus('Select a discipline first, then enable Telehealth for that discipline.');
                          return;
                        }
                        const codes = teacherOptions.programRequiredCodes || [];
                        if (!codes.includes(selectedProg)) {
                          setStatus(
                            `${getDisciplineShort(selectedProg)} is not assigned for this student – cannot use Telehealth for this discipline.`
                          );
                          return;
                        }
                      }
                      setIsEvent(false);
                    }

                    setIsTelehealth(next);
                  }}
                  disabled={!teacherOptions.telehealth_enabled}
                  className={
                    "px-2 py-0.5 rounded-full text-xs border flex items-center gap-1 " +
                    (!teacherOptions.telehealth_enabled
                      ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : isTelehealth
                        ? "bg-rose-500 text-white border-rose-500"
                        : "bg-white text-gray-800 border-gray-300 hover:bg-rose-50")
                  }
                  title={
                    !teacherOptions.telehealth_enabled
                      ? "Telehealth is not available for this student/teachers."
                      : telehealthLabel || 'Telehealth'
                  }
                >
                  <svg
                    className="w-3 h-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="3" y="5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M17 9L21 7V17L17 15V9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  <span>{telehealthLabel || 'Telehealth'}</span>
                </button>
              </div>
              {!(teacherOptions.programRequiredCodes || []).length && (
                <div className="mt-1 text-xs text-gray-500">
                  This student has no specific disciplines assigned yet – EI/PI fallback will be used.
                </div>
              )}
            </div>

            {/* Default teacher blok */}
            {teacherOptions.default_teacher && (() => {
              const t = teacherOptions.default_teacher;
              const tidNum = getTidNum(t);
              const codes = t.program_codes || [];
              const isEventMode = isEvent;
              const hasDiscFilter =
                !isEventMode &&
                (teacherOptions.programRequiredCodes || []).length > 0 &&
                !!selectedProg;

              if (hasDiscFilter && !codes.includes(selectedProg)) {
                return null;
              }

              const suggestion = suggestInterval(
                modalDate,
                modalFrom,
                modalMaxTo,
                tidNum,
                isEventMode,
                isEventMode ? null : selectedProg
              );

              const showWarnings = !suggestion.okTeacher || !suggestion.okStudent;

              const teacherHint = !suggestion.okTeacher
                ? teacherCoverageHint(modalDate, tidNum, modalFrom, suggestion.to)
                : '';
              const studentHint = !suggestion.okStudent
                ? studentCoverageHint(modalDate, modalFrom, suggestion.to)
                : '';

              const progForReasons =
                (!isEventMode && selectedProg) ||
                teacherOptions.programSelected ||
                teacherOptions.programRequired ||
                (teacherOptions.programRequiredCodes || [])[0] ||
                null;

              const reasonsList = (() => {
                const raw = t._match?.reasons || [];
                return raw.map(r => {
                  if (r.startsWith('discipline:') && progForReasons && progForReasons.startsWith('PROG_')) {
                    return `discipline:${progForReasons.replace('PROG_', '')}`;
                  }
                  return r;
                });
              })();

              const disabledByTelehealth =
                isTelehealth &&
                (!teacherOptions.telehealth_enabled || !t.can_telehealth);

              return (
                <div className="mb-4 border p-2 rounded bg-green-100">
                  <div className="font-semibold">Default Teacher</div>

                  {/* Badge-ovi za discipline teachera */}
                  {(t.program_codes || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                      {(t.program_codes || []).map(code => (
                        <span
                          key={code}
                          className="px-1.5 py-0.5 rounded-full border bg-white text-gray-700"
                        >
                          {getDisciplineShort(code)}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="text-sm mt-1">
                    <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                    <b>Travel:</b>{' '}
                    {typeof t.distance_miles === "number"
                      ? `${t.distance_miles.toFixed(2)} mi × $${t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = $${t.travel_cost?.toFixed?.(2) ?? '—'}`
                      : <span className="text-xs text-gray-500">Not calculated</span>}
                    <br />
                    <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                    <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                    <span className="text-xs text-gray-500">{t.cost_breakdown}</span>
                  </div>

                  {t._match && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                        Match score: {t._match.score}
                      </span>
                      {!!t._match.reasons?.length && (
                        <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                          {reasonsList.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {showWarnings && (
                    <div className="text-red-600 text-xs mt-1">
                      {!suggestion.okTeacher && <div>{teacherHint}</div>}
                      {!suggestion.okStudent && <div>Student: {studentHint}</div>}
                    </div>
                  )}

                  {isTelehealth && !disabledByTelehealth && (
                    <div className="mt-1 text-xs text-rose-700">
                      Telehealth will be used for this visit.
                    </div>
                  )}
                  {isTelehealth && disabledByTelehealth && (
                    <div className="mt-1 text-xs text-red-700">
                      This teacher is not enabled for Telehealth with this student.
                    </div>
                  )}

                  <button
                    className={
                      "mt-2 px-2 py-1 rounded " +
                      (disabledByTelehealth
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-green-500 hover:bg-green-600 text-white")
                    }
                    onClick={() => {
                      if (disabledByTelehealth) return;
                      if (!suggestion.okTeacher || !suggestion.okStudent) return;
                      selectTeacherInModal(t);
                    }}
                    disabled={disabledByTelehealth || !suggestion.okTeacher || !suggestion.okStudent}
                  >
                    {t.name}
                  </button>
                </div>
              );
            })()}

            {/* Alternatives */}
            {(() => {
              const isEventMode = isEvent;
              const hasDiscFilter =
                !isEventMode &&
                (teacherOptions.programRequiredCodes || []).length > 0 &&
                !!selectedProg;

              const allAlts = teacherOptions.alternatives || [];
              if (!allAlts.length) {
                return <p>No alternative teachers available.</p>;
              }

              const altFiltered = hasDiscFilter
                ? allAlts.filter(t => (t.program_codes || []).includes(selectedProg))
                : allAlts;

              if (hasDiscFilter && !altFiltered.length) {
                return (
                  <p className="text-sm text-gray-600">
                    No alternative teachers match the selected discipline.
                  </p>
                );
              }

              const progForReasons =
                (!isEventMode && selectedProg) ||
                teacherOptions.programSelected ||
                teacherOptions.programRequired ||
                (teacherOptions.programRequiredCodes || [])[0] ||
                null;

              return altFiltered.map(t => {
                const tidNum = getTidNum(t);

                const suggestion = suggestInterval(
                  modalDate,
                  modalFrom,
                  modalMaxTo,
                  tidNum,
                  isEventMode,
                  isEventMode ? null : selectedProg
                );

                const showWarnings = !suggestion.okTeacher || !suggestion.okStudent;

                const teacherHint = !suggestion.okTeacher
                  ? teacherCoverageHint(modalDate, tidNum, modalFrom, suggestion.to)
                  : '';
                const studentHint = !suggestion.okStudent
                  ? studentCoverageHint(modalDate, modalFrom, suggestion.to)
                  : '';

                const reasonsList = (() => {
                  const raw = t._match?.reasons || [];
                  return raw.map(r => {
                    if (r.startsWith('discipline:') && progForReasons && progForReasons.startsWith('PROG_')) {
                      return `discipline:${progForReasons.replace('PROG_', '')}`;
                    }
                    return r;
                  });
                })();

                const disabledByTelehealth =
                  isTelehealth &&
                  (!teacherOptions.telehealth_enabled || !t.can_telehealth);

                return (
                  <div key={getTid(t)} className="mb-2 border p-2 rounded bg-yellow-100">
                    <div className="font-semibold">{t.name}</div>

                    {/* Badge-ovi za discipline teachera */}
                    {(t.program_codes || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                        {(t.program_codes || []).map(code => (
                          <span
                            key={code}
                            className="px-1.5 py-0.5 rounded-full border bg-white text-gray-700"
                          >
                            {getDisciplineShort(code)}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="text-sm mt-1">
                      <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                      <b>Travel:</b>{' '}
                      {t.travel_cost_unknown ? (
                        <span className="text-xs text-yellow-700 font-semibold">
                          Travel cost not calculated – teacher has no prior visits that day or matrix unavailable
                        </span>
                      ) : typeof t.distance_miles === "number" ? (
                        <span>
                          {t.distance_miles.toFixed(2)} mi × ${t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = ${t.travel_cost?.toFixed?.(2) ?? '—'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">Not calculated</span>
                      )}
                      <br />
                      <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                      <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                      <span className="text-xs text-gray-500">{t.cost_breakdown}</span>
                    </div>

                    {t._match && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                          Match score: {t._match.score}
                        </span>
                        {!!t._match.reasons?.length && (
                          <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                            {reasonsList.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                          </ul>
                        )}
                      </div>
                    )}

                    {showWarnings && (
                      <div className="text-red-600 text-xs mt-1">
                        {!suggestion.okTeacher && <div>{teacherHint}</div>}
                        {!suggestion.okStudent && <div>Student: {studentHint}</div>}
                      </div>
                    )}

                    {isTelehealth && !disabledByTelehealth && (
                      <div className="mt-1 text-xs text-rose-700">
                        Telehealth will be used for this visit.
                      </div>
                    )}
                    {isTelehealth && disabledByTelehealth && (
                      <div className="mt-1 text-xs text-red-700">
                        This teacher is not enabled for Telehealth with this student.
                      </div>
                    )}

                    <button
                      className={
                        "mt-2 px-2 py-1 rounded " +
                        (disabledByTelehealth
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-yellow-500 hover:bg-yellow-600 text-white")
                      }
                      onClick={() => {
                        if (disabledByTelehealth) return;
                        if (!suggestion.okTeacher || !suggestion.okStudent) return;
                        selectTeacherInModal(t);
                      }}
                      disabled={disabledByTelehealth || !suggestion.okTeacher || !suggestion.okStudent}
                    >
                      Select
                    </button>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Telehealth link modal */}
      {showTelehealthModal && pendingTelehealth && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 text-xl"
              onClick={() => {
                setShowTelehealthModal(false);
                setPendingTelehealth(null);
                setTelehealthLink('');
              }}
            >
              ×
            </button>
            <h3 className="text-lg font-semibold mb-2">Telehealth link</h3>
            <p className="text-sm text-gray-700 mb-2">
              Paste the online meeting link for this telehealth visit.
            </p>
            <input
              type="url"
              className="border rounded w-full px-2 py-1 text-sm mb-3"
              placeholder="https://your-meeting-link..."
              value={telehealthLink}
              onChange={e => setTelehealthLink(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 rounded border border-gray-300 text-gray-700"
                onClick={() => {
                  setShowTelehealthModal(false);
                  setPendingTelehealth(null);
                  setTelehealthLink('');
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                disabled={!telehealthLink.trim()}
                onClick={() => {
                  const { date, from, to, teacher, slotType, prog, cfg } = pendingTelehealth;
                  addLocalSlot(
                    date,
                    from,
                    to,
                    teacher,
                    slotType,
                    prog,
                    cfg,
                    'telehealth',
                    telehealthLink.trim()
                  );
                  setShowTelehealthModal(false);
                  setPendingTelehealth(null);
                  setTelehealthLink('');
                  setIsTelehealth(false);
                  setSelectedProg(null);
                }}
              >
                Save Telehealth visit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parent INFO modal (read-only) */}
      {showInfoModal && isParent && infoSlot && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-4 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 text-xl"
              onClick={() => {
                setShowInfoModal(false);
                setInfoSlot(null);
                setInfoSlotDate('');
              }}
            >
              ×
            </button>
            <h3 className="text-lg font-semibold mb-2">Visit details</h3>
            <div className="text-sm space-y-1">
              <div><b>Date:</b> {infoSlotDate}</div>
              <div><b>Time:</b> {infoSlot.from}–{infoSlot.to}</div>
              <div><b>Teacher:</b> {infoSlot.teacher?.name || '—'}</div>
              <div className="flex items-center gap-2">
                <b>Type:</b>
                <DisciplineBadge program_code={infoSlot.program_code} type={infoSlot.type} />
              </div>
              <div className="flex items-center gap-2">
                <b>Status:</b>
                <StatusBadge status={infoSlot.status || 'draft'} />
              </div>
              {infoSlot.delivery_mode === 'telehealth' && (
                <div className="flex items-center gap-2 text-xs text-rose-700">
                  <svg
                    className="w-3 h-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="3" y="5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M17 9L21 7V17L17 15V9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  <span>Telehealth visit</span>
                </div>
              )}
              {infoSlot.delivery_mode === 'telehealth' && infoSlot.telehealth_link && (
                <div className="text-xs mt-1">
                  <b>Link:</b>{' '}
                  <a
                    href={infoSlot.telehealth_link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    {infoSlot.telehealth_link}
                  </a>
                </div>
              )}
              {infoSlot.reassigned_status && infoSlot.reassigned_status !== 'none' && (
                <div className="flex items-center gap-2">
                  <b>Substitute:</b>
                  <ReassignedBadge reassigned={infoSlot.reassigned_status} />
                </div>
              )}
              {infoSlot.notes && (
                <div className="mt-2">
                  <b>Notes:</b>
                  <div className="border rounded px-2 py-1 bg-gray-50 text-xs">
                    {infoSlot.notes}
                  </div>
                </div>
              )}
              <div className="mt-3 text-xs text-gray-500">
                If you need to change or cancel this visit, please contact your agency directly.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
















